import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  MapPinIcon,
  DocumentIcon,
  PaperClipIcon,
  CogIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageModal from '../common/ImageModal';
import toast from 'react-hot-toast';
import { customersAPI, usersAPI, industriesAPI, companiesAPI, leadsAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';

const CustomerForm = ({ customerId, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const { companyData, isStorageLimitExceeded } = useCompany();

  const [formData, setFormData] = useState({
    // Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    website: '',

    // Social Media
    socialMedia: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },

    // Address Information
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },

    // Professional Information
    companyName: '',
    jobTitle: '',
    department: '',
    industry: '',

    // Customer Details
    customerType: 'individual',
    status: 'active',
    lifetimeValue: 0,
    annualRevenue: 0,
    companySize: '1-10',
    source: 'website',
    notes: '',
    tags: [],

    // Preferences
    preferences: {
      communicationMethod: 'email',
      marketingOptIn: true
    },

    // Custom Fields (will be populated from company settings)
    customFields: {}
  });

  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [activeTab, setActiveTab] = useState('preview');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Modal state for image preview
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState(null);
  const [modalFile, setModalFile] = useState(null);

  // Delete modal states
  const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  useEffect(() => {
    if (companyData?.industry) {
      fetchInitialData();
    }
    if (customerId) {
      fetchCustomer();
    }
  }, [customerId, companyData?.industry]);

  const fetchInitialData = async () => {
    try {
      const [usersResponse, companyFieldsResponse] = await Promise.all([
        leadsAPI.getAssignableUsers(),
        companiesAPI.getCustomFields('customer')
      ]);

      setUsers(usersResponse.data.users || []);

      // Use only company custom fields for customers
      const companyFields = companyFieldsResponse.data.customFields || [];
      setCustomFields(companyFields);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load form data');
    }
  };

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getById(customerId);
      const customer = response.data;

      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        secondaryPhone: customer.secondaryPhone || '',
        website: customer.website || '',
        socialMedia: customer.socialMedia || {
          linkedin: '',
          twitter: '',
          facebook: '',
          instagram: ''
        },
        address: customer.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        companyName: customer.companyName || '',
        jobTitle: customer.jobTitle || '',
        department: customer.department || '',
        industry: customer.industry || '',
        customerType: customer.customerType || 'individual',
        status: customer.status || 'active',
        lifetimeValue: customer.lifetimeValue || 0,
        annualRevenue: customer.annualRevenue || 0,
        companySize: customer.companySize || '1-10',
        source: customer.source || 'website',
        notes: customer.notes || '',
        tags: customer.tags || [],
        preferences: customer.preferences || {
          communicationMethod: 'email',
          marketingOptIn: true
        },
        customFields: customer.customFields || {}
      });

      setAttachments(customer.attachments || []);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldName]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check storage quota before attempting upload
    if (isStorageLimitExceeded()) {
      toast.error('Storage limit exceeded. Please upgrade your plan or delete some files to free up space.');
      event.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      await customersAPI.uploadAttachment(customerId, formDataUpload);
      toast.success('File uploaded successfully');

      // Refresh attachments to get the complete list
      const attachmentsResponse = await customersAPI.getAttachments(customerId);
      setAttachments(attachmentsResponse.data || []);
    } catch (error) {
      console.error('Error uploading file:', error);

      // Handle quota-related errors specifically
      if (error.response?.status === 413 && error.response?.data?.quotaType === 'storage') {
        const quotaData = error.response.data;
        const currentUsage = quotaData.currentUsage || 0;
        const limit = quotaData.limit || 0;
        const available = Math.max(0, limit - currentUsage);

        toast.error(
          `Storage limit exceeded. Current usage: ${currentUsage} MB, Limit: ${limit} MB, Available: ${available} MB. Please upgrade your plan or delete some files.`
        );
      } else {
        toast.error('Failed to upload file');
      }
    } finally {
      setUploadingFile(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteAttachment = (attachment) => {
    setDeletingAttachment(attachment);
    setShowDeleteAttachmentModal(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!deletingAttachment) return;

    try {
      await customersAPI.deleteAttachment(customerId, deletingAttachment.fileId);
      setAttachments(prev => prev.filter(att => att.fileId !== deletingAttachment.fileId));
      toast.success('Attachment deleted successfully');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setShowDeleteAttachmentModal(false);
      setDeletingAttachment(null);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await customersAPI.downloadAttachment(customerId, attachment.fileId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Validation functions
  const validateField = (fieldName, value) => {
    let error = '';

    switch (fieldName) {
      case 'firstName':
        if (!value || value.trim().length === 0) {
          error = 'First name is required';
        } else if (value.trim().length < 2) {
          error = 'First name must be at least 2 characters';
        } else if (value.length > 50) {
          error = 'First name cannot exceed 50 characters';
        }
        break;

      case 'lastName':
        if (!value || value.trim().length === 0) {
          error = 'Last name is required';
        } else if (value.trim().length < 2) {
          error = 'Last name must be at least 2 characters';
        } else if (value.length > 50) {
          error = 'Last name cannot exceed 50 characters';
        }
        break;

      case 'email':
        if (value && value.trim().length > 0) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value.trim())) {
            error = 'Please enter a valid email address';
          }
        }
        break;

      case 'phone':
        if (value && value.trim()) {
          const phoneRegex = /^[\+]?[\d\s\-\(\)\.]{10,}$/;
          if (!phoneRegex.test(value.trim())) {
            error = 'Please enter a valid phone number';
          }
        }
        break;

      case 'secondaryPhone':
        if (value && value.trim()) {
          const phoneRegex = /^[\+]?[\d\s\-\(\)\.]{10,}$/;
          if (!phoneRegex.test(value.trim())) {
            error = 'Please enter a valid phone number';
          }
        }
        break;

      case 'website':
        if (value && value.trim()) {
          try {
            new URL(value.trim());
          } catch {
            error = 'Please enter a valid URL (e.g., https://example.com)';
          }
        }
        break;

      default:
        // Handle social media URLs
        if (fieldName.includes('socialMedia.')) {
          const urlField = fieldName.split('.')[1];
          if (value && value.trim()) {
            try {
              new URL(value.trim());
            } catch {
              error = `Please enter a valid ${urlField} URL`;
            }
          }
        }
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    // Validate required fields
    const requiredFields = ['firstName', 'lastName'];
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    // Validate optional but validated fields
    const optionalValidatedFields = ['phone', 'secondaryPhone', 'website'];
    optionalValidatedFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    // Validate social media URLs
    Object.keys(formData.socialMedia).forEach(socialField => {
      const error = validateField(`socialMedia.${socialField}`, formData.socialMedia[socialField]);
      if (error) {
        newErrors[`socialMedia.${socialField}`] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleValidatedInputChange = (field, value, isNested = false, parentField = null) => {
    if (isNested) {
      handleNestedInputChange(parentField, field, value);
      const error = validateField(`${parentField}.${field}`, value);
      setErrors(prev => ({
        ...prev,
        [`${parentField}.${field}`]: error
      }));
    } else {
      handleInputChange(field, value);
      const error = validateField(field, value);
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }

    // Mark field as touched
    if (isNested) {
      setTouched(prev => ({
        ...prev,
        [`${parentField}.${field}`]: true
      }));
    } else {
      setTouched(prev => ({
        ...prev,
        [field]: true
      }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      toast.error('Please correct the validation errors before submitting');
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        lifetimeValue: parseFloat(formData.lifetimeValue) || 0,
        annualRevenue: parseFloat(formData.annualRevenue) || 0
      };

      if (customerId) {
        await customersAPI.update(customerId, submitData);
        toast.success('Customer updated successfully');
      } else {
        await customersAPI.create(submitData);
        toast.success('Customer created successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error(error.response?.data?.message || 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (filename) => {
    const extension = (filename || '').split('.').pop().toLowerCase();
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const documentTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

    if (imageTypes.includes(extension)) return 'image';
    if (documentTypes.includes(extension)) return 'document';
    return 'other';
  };

  const getFileIcon = (filename) => {
    const extension = (filename || '').split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <DocumentIcon className="h-5 w-5 text-red-500" />;
      case 'doc':
      case 'docx':
        return <DocumentIcon className="h-5 w-5 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <PaperClipIcon className="h-5 w-5 text-green-500" />;
      default:
        return <PaperClipIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Helper component to fetch blob preview and render image with cleanup
  const BlobImage = ({ customerId, fileId, alt, className }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
      let mounted = true;
      let objectUrl;

      const fetchPreview = async () => {
        try {
          const response = await customersAPI.previewAttachment(customerId, fileId);
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
          objectUrl = window.URL.createObjectURL(blob);
          if (mounted) setSrc(objectUrl);
        } catch (err) {
          console.error('Preview fetch error:', err);
        }
      };

      if (customerId && fileId) fetchPreview();

      return () => {
        mounted = false;
        if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      };
    }, [customerId, fileId]);

    if (!src) return null;
    return <img src={src} alt={alt} className={className} />;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 px-4 lg:px-6">

        <form onSubmit={handleSubmit} className="space-y-8 pt-8">
            {/* Contact Information Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 overflow-hidden mb-8">
              <div className="relative overflow-hidden bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 via-indigo-400/30 to-purple-400/30"></div>
                <div className="relative flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                    <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">👤 Contact Information</h2>
                    <p className="text-blue-100 text-xs sm:text-sm">Basic contact details and communication info</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    👤 First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleValidatedInputChange('firstName', e.target.value)}
                      onBlur={() => handleBlur('firstName')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-blue-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.firstName
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-blue-200 focus:ring-blue-300/50 focus:border-blue-400'
                      }`}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && touched.firstName && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.firstName && touched.firstName && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    👥 Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleValidatedInputChange('lastName', e.target.value)}
                      onBlur={() => handleBlur('lastName')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-cyan-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.lastName
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-cyan-200 focus:ring-cyan-300/50 focus:border-cyan-400'
                      }`}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && touched.lastName && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.lastName && touched.lastName && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.lastName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📧 Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleValidatedInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-teal-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.email
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-teal-200 focus:ring-teal-300/50 focus:border-teal-400'
                      }`}
                      placeholder="Enter email address"
                    />
                    {errors.email && touched.email && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.email && touched.email && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📞 Phone
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleValidatedInputChange('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-green-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.phone
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-green-200 focus:ring-green-300/50 focus:border-green-400'
                      }`}
                      placeholder="Enter phone number"
                    />
                    {errors.phone && touched.phone && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.phone && touched.phone && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📞 Secondary Phone
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.secondaryPhone}
                      onChange={(e) => handleValidatedInputChange('secondaryPhone', e.target.value)}
                      onBlur={() => handleBlur('secondaryPhone')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-purple-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.secondaryPhone
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-purple-200 focus:ring-purple-300/50 focus:border-purple-400'
                      }`}
                      placeholder="Enter secondary phone number"
                    />
                    {errors.secondaryPhone && touched.secondaryPhone && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.secondaryPhone && touched.secondaryPhone && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.secondaryPhone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🌐 Website
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleValidatedInputChange('website', e.target.value)}
                      onBlur={() => handleBlur('website')}
                      className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-pink-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                        errors.website
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                          : 'border-pink-200 focus:ring-pink-300/50 focus:border-pink-400'
                      }`}
                      placeholder="https://example.com"
                    />
                    {errors.website && touched.website && (
                      <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.website && touched.website && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                      {errors.website}
                    </p>
                  )}
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-10 pt-8 border-t-2 border-gradient-to-r from-blue-200 to-cyan-200">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 animate-pulse"></div>
                  🌐 Social Media Profiles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      💼 LinkedIn
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.socialMedia.linkedin}
                        onChange={(e) => handleValidatedInputChange('linkedin', e.target.value, true, 'socialMedia')}
                        onBlur={() => handleBlur('socialMedia.linkedin')}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-blue-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                          errors['socialMedia.linkedin']
                            ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                            : 'border-blue-200 focus:ring-blue-300/50 focus:border-blue-400'
                        }`}
                        placeholder="https://linkedin.com/in/username"
                      />
                      {errors['socialMedia.linkedin'] && touched['socialMedia.linkedin'] && (
                        <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                      )}
                    </div>
                    {errors['socialMedia.linkedin'] && touched['socialMedia.linkedin'] && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                        {errors['socialMedia.linkedin']}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      🐦 Twitter
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.socialMedia.twitter}
                        onChange={(e) => handleValidatedInputChange('twitter', e.target.value, true, 'socialMedia')}
                        onBlur={() => handleBlur('socialMedia.twitter')}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-cyan-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                          errors['socialMedia.twitter']
                            ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                            : 'border-cyan-200 focus:ring-cyan-300/50 focus:border-cyan-400'
                        }`}
                        placeholder="https://twitter.com/username"
                      />
                      {errors['socialMedia.twitter'] && touched['socialMedia.twitter'] && (
                        <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                      )}
                    </div>
                    {errors['socialMedia.twitter'] && touched['socialMedia.twitter'] && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                        {errors['socialMedia.twitter']}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📘 Facebook
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.socialMedia.facebook}
                        onChange={(e) => handleValidatedInputChange('facebook', e.target.value, true, 'socialMedia')}
                        onBlur={() => handleBlur('socialMedia.facebook')}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-teal-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                          errors['socialMedia.facebook']
                            ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                            : 'border-teal-200 focus:ring-teal-300/50 focus:border-teal-400'
                        }`}
                        placeholder="https://facebook.com/username"
                      />
                      {errors['socialMedia.facebook'] && touched['socialMedia.facebook'] && (
                        <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                      )}
                    </div>
                    {errors['socialMedia.facebook'] && touched['socialMedia.facebook'] && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                        {errors['socialMedia.facebook']}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      📷 Instagram
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={formData.socialMedia.instagram}
                        onChange={(e) => handleValidatedInputChange('instagram', e.target.value, true, 'socialMedia')}
                        onBlur={() => handleBlur('socialMedia.instagram')}
                        className={`w-full px-3 py-2 sm:px-4 sm:py-3 pr-10 bg-gradient-to-r from-white to-green-50/50 border-2 rounded-xl sm:rounded-2xl focus:ring-4 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base ${
                          errors['socialMedia.instagram']
                            ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-gradient-to-r from-red-50 to-red-50/50'
                            : 'border-green-200 focus:ring-green-300/50 focus:border-green-400'
                        }`}
                        placeholder="https://instagram.com/username"
                      />
                      {errors['socialMedia.instagram'] && touched['socialMedia.instagram'] && (
                        <ExclamationCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-red-500" />
                      )}
                    </div>
                    {errors['socialMedia.instagram'] && touched['socialMedia.instagram'] && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <ExclamationCircleIcon className="h-3 w-3 mr-1" />
                        {errors['socialMedia.instagram']}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Address Information Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-8">
              <div className="relative overflow-hidden bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
                <div className="absolute inset-0 bg-gradient-to-r from-green-400/30 via-emerald-400/30 to-teal-400/30"></div>
                <div className="relative flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                    <MapPinIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">📍 Address Information</h2>
                    <p className="text-green-100 text-xs sm:text-sm">Location and contact details</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2 lg:col-span-4">
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏠 Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleNestedInputChange('address', 'street', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter street address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏙️ City
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleNestedInputChange('address', 'city', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-emerald-50/50 border-2 border-emerald-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-emerald-300/50 focus:border-emerald-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🗺️ State/Province
                  </label>
                  <input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => handleNestedInputChange('address', 'state', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter state/province"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📮 ZIP/Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.address.zipCode}
                    onChange={(e) => handleNestedInputChange('address', 'zipCode', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter ZIP/postal code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🌍 Country
                  </label>
                  <input
                    type="text"
                    value={formData.address.country}
                    onChange={(e) => handleNestedInputChange('address', 'country', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter country"
                  />
                </div>
              </div>
              </div>
            </div>

            {/* Professional Information Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-8">
              <div className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400/30 via-violet-400/30 to-indigo-400/30"></div>
                <div className="relative flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                    <BuildingOfficeIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">🏢 Professional Information</h2>
                    <p className="text-purple-100 text-xs sm:text-sm">Company and job details</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏢 Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-purple-50/50 border-2 border-purple-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-purple-300/50 focus:border-purple-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    💼 Job Title
                  </label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-pink-50/50 border-2 border-pink-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-pink-300/50 focus:border-pink-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter job title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏭 Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-indigo-50/50 border-2 border-indigo-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-indigo-300/50 focus:border-indigo-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter department"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏗️ Industry
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-violet-50/50 border-2 border-violet-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-violet-300/50 focus:border-violet-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder="Enter industry"
                  />
                </div>
              </div>
              </div>
            </div>

            {/* Customer Details Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-8">
              <div className="relative overflow-hidden bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400/30 via-cyan-400/30 to-blue-400/30"></div>
                <div className="relative flex items-center space-x-3 sm:space-x-4">
                  <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                    <CogIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">⚙️ Customer Details</h2>
                    <p className="text-teal-100 text-xs sm:text-sm">Customer type, status, and preferences</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    👤 Customer Type
                  </label>
                  <select
                    value={formData.customerType}
                    onChange={(e) => handleInputChange('customerType', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    📊 Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🎯 Source
                  </label>
                  <select
                    value={formData.source}
                    onChange={(e) => handleInputChange('source', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-blue-50/50 border-2 border-blue-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-blue-300/50 focus:border-blue-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                  >
                    <option value="website">Website</option>
                    <option value="referral">Referral</option>
                    <option value="social_media">Social Media</option>
                    <option value="email_campaign">Email Campaign</option>
                    <option value="phone_call">Phone Call</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    💰 Lifetime Value ({companyData?.currency?.symbol || '$'})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.lifetimeValue}
                    onChange={(e) => handleInputChange('lifetimeValue', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-green-50/50 border-2 border-green-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-green-300/50 focus:border-green-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder={`0.00 ${companyData?.currency?.code || 'USD'}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    💵 Annual Revenue ({companyData?.currency?.symbol || '$'})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.annualRevenue}
                    onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-emerald-50/50 border-2 border-emerald-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-emerald-300/50 focus:border-emerald-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    placeholder={`0.00 ${companyData?.currency?.code || 'USD'}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 flex items-center">
                    🏢 Company Size
                  </label>
                  <select
                    value={formData.companySize}
                    onChange={(e) => handleInputChange('companySize', e.target.value)}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-teal-50/50 border-2 border-teal-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-teal-300/50 focus:border-teal-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                  >
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>

              </div>

              {/* Preferences */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full mr-3"></div>
                  📞 Communication Preferences
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 flex items-center">
                      💬 Preferred Method
                    </label>
                    <select
                      value={formData.preferences.communicationMethod}
                      onChange={(e) => handleNestedInputChange('preferences', 'communicationMethod', e.target.value)}
                      className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-cyan-50/50 border-2 border-cyan-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-cyan-300/50 focus:border-cyan-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="sms">SMS</option>
                      <option value="mail">Mail</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="marketingOptIn"
                      checked={formData.preferences.marketingOptIn}
                      onChange={(e) => handleNestedInputChange('preferences', 'marketingOptIn', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="marketingOptIn" className="ml-2 text-sm text-gray-700 font-bold">
                      📧 Marketing Opt-in
                    </label>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  🏷️ Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200 font-medium"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-orange-600 hover:text-orange-800 transition-colors duration-200"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-4 py-3 lg:px-5 lg:py-4 bg-gradient-to-r from-white to-orange-50/50 border-2 border-orange-200 rounded-lg sm:rounded-xl lg:rounded-2xl focus:ring-4 focus:ring-orange-300/50 focus:border-orange-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base lg:text-lg"
                    placeholder="Add a tag..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-3 lg:px-6 lg:py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg sm:rounded-xl lg:rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 text-sm lg:text-base"
                  >
                    ➕ Add
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-violet-500 rounded-full mr-3"></div>
                  📝 Notes
                </h3>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-violet-50/50 border-2 border-violet-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-violet-300/50 focus:border-violet-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base resize-none"
                  placeholder="Enter any additional notes about this customer"
                />
              </div>
              </div>
            </div>

            {/* Custom Fields Card */}
            {customFields.length > 0 && (
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-8">
                <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400/30 via-amber-400/30 to-yellow-400/30"></div>
                  <div className="relative flex items-center space-x-3 sm:space-x-4">
                    <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm border border-white/30">
                      <DocumentIcon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">📄 Custom Fields</h2>
                      <p className="text-orange-100 text-xs sm:text-sm">Additional custom information</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customFields.map(field => (
                    <div key={field.fieldName || 'unknown_field'}>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {field.fieldName || 'Field'}
                      </label>
                      {field.fieldType === 'text' && (
                        <input
                          type="text"
                          value={formData.customFields[field.fieldName] || ''}
                          onChange={(e) => handleCustomFieldChange(field.fieldName || 'unknown_field', e.target.value)}
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-orange-50/50 border-2 border-orange-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-orange-300/50 focus:border-orange-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                          placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
                        />
                      )}
                      {field.fieldType === 'number' && (
                        <input
                          type="number"
                          value={formData.customFields[field.fieldName] || ''}
                          onChange={(e) => handleCustomFieldChange(field.fieldName || 'unknown_field', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-amber-50/50 border-2 border-amber-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-amber-300/50 focus:border-amber-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                          placeholder={field.placeholder || `Enter ${(field.fieldName || 'field').toLowerCase()}`}
                        />
                      )}
                      {field.fieldType === 'date' && (
                        <input
                          type="date"
                          value={formData.customFields[field.fieldName] || ''}
                          onChange={(e) => handleCustomFieldChange(field.fieldName || 'unknown_field', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                      {field.fieldType === 'dropdown' && (
                        <select
                          value={formData.customFields[field.fieldName] || ''}
                          onChange={(e) => handleCustomFieldChange(field.fieldName || 'unknown_field', e.target.value)}
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-white to-yellow-50/50 border-2 border-yellow-200 rounded-xl sm:rounded-2xl focus:ring-4 focus:ring-yellow-300/50 focus:border-yellow-400 transition-all duration-300 backdrop-blur-sm shadow-sm focus:shadow-lg text-sm sm:text-base"
                        >
                          <option value="">Select {(field.fieldName || 'option').toLowerCase()}</option>
                          {field.options?.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
                </div>
              </div>
            )}

            {/* Documents & Attachments Card */}
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 overflow-hidden mb-8">
              <div className="relative overflow-hidden bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 px-4 py-3 sm:px-6 sm:py-4">
                <div className="absolute inset-0 bg-gradient-to-r from-red-400/30 via-rose-400/30 to-pink-400/30"></div>
                <div className="relative flex items-center space-x-2 sm:space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                    <PaperClipIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white">📎 Documents & Attachments</h2>
                    <p className="text-red-100 text-xs">Upload and manage files</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-4">
                  {/* Upload Area */}
                  <div className="relative border-2 border-dashed border-red-300 rounded-2xl p-8 text-center bg-gradient-to-br from-red-50/50 to-rose-50/50 hover:from-red-100/50 hover:to-rose-100/50 transition-all duration-300 backdrop-blur-sm">
                    <div className="p-3 bg-white/60 rounded-xl backdrop-blur-sm border border-white/40 inline-block mb-4">
                      <PaperClipIcon className="h-10 w-10 text-red-500 mx-auto" />
                    </div>
                    <div className="space-y-2">
                      {!customerId ? (
                        <>
                          <p className="text-gray-700 font-semibold text-base">📝 File upload available after customer creation</p>
                          <p className="text-xs text-gray-600">Create the customer first, then you can upload documents</p>
                        </>
                      ) : (
                        <>
                          <p className="text-gray-700 font-semibold text-base">📤 Drop files here or click to upload</p>
                          <p className="text-xs text-gray-600">Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 2MB)</p>
                        </>
                      )}
                    </div>
                    {customerId && (
                      <input
                        type="file"
                        name="file"
                        onChange={handleFileUpload}
                        onClick={(e) => e.stopPropagation()}
                        disabled={uploadingFile}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      />
                    )}
                    {uploadingFile && (
                      <div className="mt-6">
                        <LoadingSpinner size="small" text="Uploading..." />
                      </div>
                    )}
                  </div>

                  {/* Attachments Tabs */}
                  {attachments.length > 0 && (
                    <div className="mt-6">
                      {/* Tab Navigation */}
                      <div className="flex border-b border-red-200 mb-6">
                        <button
                          type='button'
                          onClick={() => setActiveTab('list')}
                          className={`px-6 py-3 font-bold text-sm border-b-3 transition-all duration-300 rounded-t-lg ${
                            activeTab === 'list'
                              ? 'border-red-500 text-red-600 bg-gradient-to-r from-red-50 to-rose-50'
                              : 'border-transparent text-gray-500 hover:text-red-600 hover:bg-red-50/50'
                          }`}
                        >
                          📋 List View
                        </button>
                        <button
                          type='button'
                          onClick={() => setActiveTab('preview')}
                          className={`px-6 py-3 font-bold text-sm border-b-3 transition-all duration-300 rounded-t-lg ${
                            activeTab === 'preview'
                              ? 'border-red-500 text-red-600 bg-gradient-to-r from-red-50 to-rose-50'
                              : 'border-transparent text-gray-500 hover:text-red-600 hover:bg-red-50/50'
                          }`}
                        >
                          👁️ Preview
                        </button>
                      </div>

                      {/* Tab Content */}
                      {activeTab === 'list' && (
                        <div className="space-y-3">
                          <h4 className="font-bold text-gray-800 flex items-center text-base">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                            📁 Uploaded Documents
                          </h4>
                          {attachments.map((attachment) => (
                            <div
                              key={attachment.fileId}
                              className="flex items-center justify-between p-4 bg-gradient-to-r from-white to-red-50/30 border-2 border-red-200 rounded-xl hover:shadow-lg transition-all duration-300 backdrop-blur-sm"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 bg-red-100 rounded-lg border border-red-200">
                                  {getFileIcon(attachment.originalName)}
                                </div>
                                <div>
                                  <p className="font-bold text-gray-800 text-sm">{attachment.originalName}</p>
                                  <p className="text-xs text-gray-600">
                                    {(attachment.size / 1024 / 1024).toFixed(2)} MB •
                                    📅 Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadAttachment(attachment)}
                                  className="p-2 text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-300 font-semibold"
                                  title="Download"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachment(attachment)}
                                  className="p-2 text-red-600 hover:text-white hover:bg-red-500 rounded-lg transition-all duration-300 font-semibold"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Preview Tab */}
                      {activeTab === 'preview' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {attachments.map((attachment) => {
                            const fileType = getFileType(attachment.originalName);
                            const isPreviewable = fileType === 'image' || fileType === 'document';

                            return (
                              <div key={attachment.fileId} className="relative group bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow duration-200">
                                <div className="aspect-square bg-gray-50 rounded-md mb-2 flex items-center justify-center overflow-hidden">
                                  {fileType === 'image' ? (
                                    <div className="w-full h-full">
                                      <BlobImage
                                        customerId={customerId}
                                        fileId={attachment.fileId}
                                        alt={attachment.originalName}
                                        className="w-full h-full object-cover cursor-pointer"
                                      />
                                      <button
                                        type='button'
                                        className="absolute inset-0 w-full h-full bg-transparent hover:bg-black/10 transition-colors duration-200"
                                        onClick={async () => {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
                                            const url = window.URL.createObjectURL(blob);
                                            setModalSrc(url);
                                            setModalFile(attachment);
                                            setModalOpen(true);
                                          } catch (err) {
                                            console.error('Error opening modal preview:', err);
                                            toast.error('Failed to load preview');
                                          }
                                        }}
                                        title="Click to preview image"
                                      />
                                    </div>
                                  ) : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-pink-50 rounded-md border border-red-200">
                                      <div className="text-red-500 mb-2">
                                        {getFileIcon(attachment.originalName)}
                                      </div>
                                      <div className="text-center px-2">
                                        <p className="text-xs font-medium text-red-700 mb-1">PDF Document</p>
                                        <p className="text-xs text-red-600 mb-2">{formatFileSize(attachment.size)}</p>
                                        <button
                                          type="button"
                                          className="px-3 py-1 bg-red-500 text-white text-xs rounded-md hover:bg-red-600 transition-colors duration-200 shadow-sm"
                                          onClick={async () => {
                                            try {
                                              const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                              const blob = new Blob([response.data], { type: 'application/pdf' });
                                              const url = window.URL.createObjectURL(blob);
                                              window.open(url, '_blank');
                                              // Clean up the blob URL after a delay
                                              setTimeout(() => {
                                                window.URL.revokeObjectURL(url);
                                              }, 1000);
                                            } catch (err) {
                                              console.error('Error opening PDF preview:', err);
                                              toast.error('Failed to load PDF preview');
                                            }
                                          }}
                                          title="Click to preview PDF"
                                        >
                                          👁️ Preview PDF
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      {getFileIcon(attachment.originalName)}
                                    </div>
                                  )}
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-900 truncate" title={attachment.originalName}>{attachment.originalName}</p>
                                  <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                                  {isPreviewable && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      {fileType === 'image' ? '🖼️ Click to preview' : fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf') ? '📄 Click to preview' : ''}
                                    </p>
                                  )}
                                </div>

                                {/* Action buttons - shown on hover */}
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                  {isPreviewable && (
                                    <button
                                      type='button'
                                      onClick={async () => {
                                        if (fileType === 'image') {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/*' });
                                            const url = window.URL.createObjectURL(blob);
                                            setModalSrc(url);
                                            setModalFile(attachment);
                                            setModalOpen(true);
                                          } catch (err) {
                                            console.error('Error opening modal preview:', err);
                                            toast.error('Failed to load preview');
                                          }
                                        } else if (fileType === 'document' && attachment.originalName.toLowerCase().endsWith('.pdf')) {
                                          try {
                                            const response = await customersAPI.previewAttachment(customerId, attachment.fileId);
                                            const blob = new Blob([response.data], { type: 'application/pdf' });
                                            const url = window.URL.createObjectURL(blob);
                                            window.open(url, '_blank');
                                            setTimeout(() => {
                                              window.URL.revokeObjectURL(url);
                                            }, 1000);
                                          } catch (err) {
                                            console.error('Error opening PDF preview:', err);
                                            toast.error('Failed to load PDF preview');
                                          }
                                        }
                                      }}
                                      className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors duration-200"
                                      title={fileType === 'image' ? 'Preview Image' : 'Preview PDF'}
                                    >
                                      <EyeIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                  <button
                                    type='button'
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-200"
                                    title="Download"
                                  >
                                    <ArrowDownTrayIcon className="h-3 w-3" />
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => handleDeleteAttachment(attachment)}
                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
                                    title="Delete"
                                  >
                                    <XMarkIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

          {/* Form Actions */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-6 mt-6">
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-gray-300 rounded-xl sm:rounded-2xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                ❌ Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl sm:rounded-2xl hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 transition-all duration-300 font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                {submitting ? '⏳ Saving...' : (customerId ? '✨ Update Customer' : '🚀 Create Customer')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {modalOpen && (
        <ImageModal
          open={modalOpen}
          src={modalSrc}
          alt={modalFile?.originalName}
          filename={modalFile?.originalName}
          onClose={() => {
            setModalOpen(false);
            if (modalSrc) {
              window.URL.revokeObjectURL(modalSrc);
              setModalSrc(null);
            }
            setModalFile(null);
          }}
          onDownload={() => handleDownloadAttachment(modalFile)}
          onDelete={async () => {
            await handleDeleteAttachment(modalFile);
            setModalOpen(false);
          }}
        />
      )}

      {/* Delete Attachment Confirmation Modal */}
      <AnimatePresence>
        {showDeleteAttachmentModal && deletingAttachment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowDeleteAttachmentModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                  <PaperClipIcon className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Delete Attachment
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingAttachment?.originalName}"</span>?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowDeleteAttachmentModal(false);
                      setDeletingAttachment(null);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDeleteAttachment}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomerForm;
